import { capitalize, StandardProps, Theme, withStyles } from '@material-ui/core';
import { Styles } from '@material-ui/styles/withStyles';
import clsx from 'clsx';
import * as React from 'react';
import { Ref } from 'react';

const SIZE = 44;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export interface CircularTimerProps extends StandardProps<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: {
    /** Styles applied to the root element. */
    root?: string;
    /** Styles applied to the root element if `variant="determinate"`. */
    determinate?: string;
    /** Styles applied to the root element if `variant="indeterminate"`. */
    indeterminate?: string;
    /** Styles applied to the root element if `color="primary"`. */
    colorPrimary?: string;
    /** Styles applied to the root element if `color="secondary"`. */
    colorSecondary?: string;
    /** Styles applied to the `svg` element. */
    svg?: string;
    /** Styles applied to the `circle` svg path. */
    circle?: string;
    /** Styles applied to the `circle` svg path if `variant="determinate"`. */
    circleDeterminate?: string;
    /** Styles applied to the `circle` svg path if `variant="indeterminate"`. */
    circleIndeterminate?: string;
    /** Styles applied to the `circle` svg path if `disableShrink={true}`. */
    circleDisableShrink?: string;
  };
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'inherit';
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   * @default 'primary'
   */
  colorUnderlay?: 'primary' | 'secondary';
  /**
   * If `true`, the shrink animation is disabled.
   * This only works if variant is `indeterminate`.
   * @default false
   */
  disableShrink?: boolean;
  /**
   * The size of the circle.
   * If using a number, the pixel unit is assumed.
   * If using a string, you need to provide the CSS unit, e.g '3rem'.
   * @default 40
   */
  size?: number | string;
  /**
   * The thickness of the circle.
   * @default 3.6
   */
  thickness?: number;
  /**
   * The value of the progress indicator for the determinate variant.
   * Value between 0 and 100.
   * @default 0
   */
  value?: number;
  /**
   * The variant to use.
   * Use indeterminate when there is no progress value.
   * @default 'indeterminate'
   */
  variant?: 'determinate' | 'indeterminate';
}

export type CircularTimerClassKey = keyof NonNullable<CircularTimerProps['classes']>;

export const styles: Styles<Theme, CircularTimerProps, CircularTimerClassKey> = (theme: Theme) => ({
  /* Styles applied to the root element. */
  root: {
    display: 'inline-block',
  },
  /* Styles applied to the root element if `variant="determinate"`. */
  determinate: {
    transition: theme.transitions.create('transform'),
  },
  /* Styles applied to the root element if `variant="indeterminate"`. */
  indeterminate: {
    animation: '$circular-rotate 1.4s linear infinite',
  },
  /* Styles applied to the root element if `color="primary"`. */
  colorPrimary: {
    color: theme.palette.primary.main,
  },
  /* Styles applied to the root element if `color="secondary"`. */
  colorSecondary: {
    color: theme.palette.secondary.main,
  },
  /* Styles applied to the `svg` element. */
  svg: {
    display: 'block', // Keeps the progress centered
  },
  /* Styles applied to the `circle` svg path. */
  circle: {
    stroke: 'currentColor',
    // Use butt to follow the specification, by chance, it's already the default CSS value.
    // strokeLinecap: 'butt',
  },
  /* Styles applied to the `circle` svg path if `variant="determinate"`. */
  circleDeterminate: {
    transition: theme.transitions.create('stroke-dashoffset'),
  },
  /* Styles applied to the `circle` svg path if `variant="indeterminate"`. */
  circleIndeterminate: {
    animation: '$circular-dash 1.4s ease-in-out infinite',
    // Some default value that looks fine waiting for the animation to kicks in.
    strokeDasharray: '80px, 200px',
    strokeDashoffset: '0px', // Add the unit to fix a Edge 16 and below bug.
  },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  '@keyframes circular-rotate': {
    '0%': {
      // Fix IE 11 wobbly
      transformOrigin: '50% 50%',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  '@keyframes circular-dash': {
    '0%': {
      strokeDasharray: '1px, 200px',
      strokeDashoffset: '0px',
    },
    '50%': {
      strokeDasharray: '100px, 200px',
      strokeDashoffset: '-15px',
    },
    '100%': {
      strokeDasharray: '100px, 200px',
      strokeDashoffset: '-125px',
    },
  },
  /* Styles applied to the `circle` svg path if `disableShrink={true}`. */
  circleDisableShrink: {
    animation: 'none',
  },
});

/**
 * ## ARIA
 *
 * If the progress bar is describing the loading progress of a particular region of a page,
 * you should use `aria-describedby` to point to the progress bar, and set the `aria-busy`
 * attribute to `true` on that region until it has finished loading.
 */
const CircularTimer: React.FC<CircularTimerProps> = React.forwardRef((props: CircularTimerProps, ref: Ref<any>) => {
  const {
    classes,
    className,
    color = 'primary',
    colorUnderlay = 'secondary',
    disableShrink = false,
    size = 40,
    style,
    thickness = 3.6,
    value = 0,
    variant = 'indeterminate',
    ...other
  } = props;

  const circleStyle: { strokeDasharray?: string; strokeDashoffset?: string } = {};
  const circleUnderlayStyle: { strokeDasharray?: string; strokeDashoffset?: string } = {};
  const rootStyle: { transform?: string } = {};
  const rootProps: { 'aria-valuenow'?: number } = {};

  if (variant === 'determinate') {
    const circumference = 2 * Math.PI * ((SIZE - thickness) / 2);
    circleStyle.strokeDasharray = circumference.toFixed(3);
    circleUnderlayStyle.strokeDasharray = circumference.toFixed(3);
    rootProps['aria-valuenow'] = Math.round(value);
    circleStyle.strokeDashoffset = `${((-value / 100) * circumference).toFixed(3)}px`;
    circleUnderlayStyle.strokeDashoffset = `${(((100 - value) / 100) * circumference).toFixed(3)}px`;
    rootStyle.transform = 'rotate(-90deg)';
  }

  return (
    <div
      className={clsx(
        classes?.root,
        {
          ...(classes
            ? {
                [classes.determinate as string]: variant === 'determinate',
                [classes.indeterminate as string]: variant === 'indeterminate',
              }
            : {}),
        },
        className,
      )}
      style={{ width: size, height: size, ...rootStyle, ...style }}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ref={ref}
      role="progressbar"
      {...rootProps}
      {...other}
    >
      <svg className={classes?.svg} viewBox={`${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`}>
        <circle
          key="main"
          className={clsx(classes?.circle, {
            ...(classes
              ? {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  [classes[`color${capitalize(color)}`] as string]: color !== 'inherit',
                  [classes.circleDeterminate as string]: variant === 'determinate',
                  [classes.circleIndeterminate as string]: variant === 'indeterminate',
                  [classes.circleDisableShrink as string]: disableShrink,
                }
              : {}),
          })}
          style={circleStyle}
          cx={SIZE}
          cy={SIZE}
          r={(SIZE - thickness) / 2}
          fill="none"
          strokeWidth={thickness}
        />
        <circle
          key="underlay"
          className={clsx(classes?.circle, {
            ...(classes
              ? {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  [classes[`color${capitalize(colorUnderlay)}`] as string]: color !== 'inherit',
                  [classes.circleDeterminate as string]: variant === 'determinate',
                  [classes.circleIndeterminate as string]: variant === 'indeterminate',
                  [classes.circleDisableShrink as string]: disableShrink,
                }
              : {}),
          })}
          style={circleUnderlayStyle}
          cx={SIZE}
          cy={SIZE}
          r={(SIZE - thickness) / 2}
          fill="none"
          strokeWidth={thickness}
        />
      </svg>
    </div>
  );
});

export default withStyles(styles, {
  name: 'CircularTimer',
  flip: false,
})(CircularTimer);
